/**
 * Shared 3D brush cursor — ring + translucent disc on the mesh surface.
 *
 * Used by both sculpt mode and sketch mode.  Each mode supplies a per-frame
 * `getStyle` callback that returns the cursor color and screen-space radius
 * in CSS pixels.  The screen-space radius is converted to world-space each
 * frame using the active camera projection and cursor depth, so the brush
 * appears the same size on screen regardless of zoom level.
 *
 * Depth testing is disabled so the cursor always renders on top of geometry.
 * Position is lerped quickly for smooth motion during heavy computation.
 * Orientation uses quaternion slerp.  During hover it tracks closely.
 * During strokes, GPU compute makes CPU-side normals unreliable, so we
 * gate updates: a new normal is only accepted if it's within a plausible
 * angle of the current orientation (rejects stale-geometry spikes while
 * still following genuine curvature like spheres).
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { screenToWorldRadius, setCameraState } from "./screenRadius.ts";

/** Cursor state written by pointer handlers every event. */
export interface BrushCursorState {
  /** Brush center in world space */
  position: THREE.Vector3;
  /** Surface normal at the hit point */
  normal: THREE.Vector3;
  /** Whether the cursor is currently visible (pen hovering or stroking) */
  visible: boolean;
  /** Whether a stroke is in progress */
  stroking: boolean;
  /** Current pen pressure during stroke */
  pressure: number;
}

/** Returned by the per-frame style callback. */
export interface BrushCursorStyle {
  color: THREE.Color;
  /** Screen-space radius in CSS pixels. */
  radius: number;
}

/* ── Visual constants (stroke-cursor style) ── */
const RING_INNER = 0.95;
const RING_OUTER = 1.0;
const SEGMENTS = 64;
const HOVER_OPACITY = 0.25;
const STROKE_OPACITY_MIN = 0.2;
const STROKE_OPACITY_MAX = 0.6;

/** Position lerp speed — high for responsive tracking. */
const POS_LERP_SPEED = 20;
/** Orientation slerp speed during hover (geometry is stable). */
const ORIENT_SLERP_HOVER = 12;
/** Orientation slerp speed during strokes (only applied to accepted normals). */
const ORIENT_SLERP_STROKE = 6;
/**
 * Max angle (radians) between the current smoothed normal and an incoming
 * stroke normal for it to be accepted.  ~30 degrees — enough for curvature,
 * rejects stale-geometry spikes.
 */
const STROKE_NORMAL_MAX_ANGLE = Math.PI / 6;

const _targetQuat = new THREE.Quaternion();
const _smoothNormal = new THREE.Vector3();
const _Z = new THREE.Vector3(0, 0, 1);

export function BrushCursor({
  cursorRef,
  getStyle,
}: {
  cursorRef: React.MutableRefObject<BrushCursorState>;
  /** Called every frame to resolve mode-specific color and scale. */
  getStyle: () => BrushCursorStyle | null;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const { camera, size } = useThree();

  // Smoothed position (fast lerp), orientation (slow slerp), and scale.
  const smoothPos = useRef(new THREE.Vector3());
  const smoothQuat = useRef(new THREE.Quaternion());
  const smoothScale = useRef(0);
  const initialised = useRef(false);

  const ringGeo = useMemo(
    () => new THREE.RingGeometry(RING_INNER, RING_OUTER, SEGMENTS),
    [],
  );

  const discGeo = useMemo(() => new THREE.CircleGeometry(1, SEGMENTS), []);

  const ringMat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: HOVER_OPACITY,
        depthWrite: false,
        depthTest: false,
        side: THREE.DoubleSide,
      }),
    [],
  );

  const discMat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.04,
        depthWrite: false,
        depthTest: false,
        side: THREE.DoubleSide,
      }),
    [],
  );

  useFrame((_state, delta) => {
    // Update module-level camera cache for non-R3F consumers
    setCameraState(camera, size.height);

    const group = groupRef.current;
    if (!group) return;

    const cursor = cursorRef.current;
    const style = getStyle();

    if (!cursor.visible || !style) {
      group.visible = false;
      initialised.current = false;
      return;
    }

    group.visible = true;

    // First update the smoothed position so we know the actual render depth.
    if (!initialised.current) {
      smoothPos.current.copy(cursor.position);
      initialised.current = true;
    } else {
      const posT = Math.min(1, POS_LERP_SPEED * delta);
      smoothPos.current.lerp(cursor.position, posT);
    }

    // Convert screen-space radius (CSS px) → world-space at the smoothed
    // position's depth.  This MUST use smoothPos (not cursor.position)
    // because that's where the ring actually renders — using the raw target
    // would cause a depth/scale mismatch during position interpolation.
    const worldRadius = screenToWorldRadius(
      style.radius,
      smoothPos.current,
      camera,
      size.height,
    );

    // Set scale directly — no lerp.  The world radius is a deterministic
    // function of the screen-px size and the current render depth; smoothing
    // it would break view-independence by introducing transient size drift.
    smoothScale.current = worldRadius;

    // Orientation: during hover, track closely (geometry is stable).
    // During strokes, gate updates — only accept normals within a
    // plausible angle of the current orientation to reject stale-geometry
    // spikes while still following genuine surface curvature.
    _targetQuat.setFromUnitVectors(_Z, cursor.normal);

    if (!cursor.stroking) {
      const orientT = Math.min(1, ORIENT_SLERP_HOVER * delta);
      smoothQuat.current.slerp(_targetQuat, orientT);
    } else {
      // Derive the current smoothed normal from the quaternion
      _smoothNormal.set(0, 0, 1).applyQuaternion(smoothQuat.current);
      const angle = _smoothNormal.angleTo(cursor.normal);
      if (angle < STROKE_NORMAL_MAX_ANGLE) {
        const orientT = Math.min(1, ORIENT_SLERP_STROKE * delta);
        smoothQuat.current.slerp(_targetQuat, orientT);
      }
    }

    // Apply position & orientation directly (no lookAt)
    group.position.copy(smoothPos.current);
    group.quaternion.copy(smoothQuat.current);

    // Scale to brush radius (smoothed)
    group.scale.setScalar(smoothScale.current);

    // Color
    ringMat.color.copy(style.color);
    discMat.color.copy(style.color);

    // Opacity based on stroke state + pressure
    if (cursor.stroking) {
      const p =
        STROKE_OPACITY_MIN +
        (STROKE_OPACITY_MAX - STROKE_OPACITY_MIN) * cursor.pressure;
      ringMat.opacity = p;
      discMat.opacity = p * 0.15;
    } else {
      ringMat.opacity = HOVER_OPACITY;
      discMat.opacity = HOVER_OPACITY * 0.15;
    }
  });

  return (
    <group
      ref={groupRef}
      userData={{ excludeFromExport: true }}
      renderOrder={999}
    >
      <mesh geometry={discGeo} material={discMat} />
      <mesh geometry={ringGeo} material={ringMat} />
    </group>
  );
}
